// src/utils/blogUtils.js

/**
 * Format date string to localized date format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date (e.g., "Jan 1, 2024")
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return '';
  }
};

/**
 * Get author initials from full name
 * @param {string} name - Author's full name
 * @returns {string} Uppercase initials or default avatar
 */
export const getAuthorInitials = (name) => {
  if (!name) return '👤';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Calculate reading time from content length
 * @param {string} content - Blog content text
 * @param {number} wordsPerMinute - Reading speed
 * @returns {number} Estimated reading time in minutes
 */
export const calculateReadingTime = (content, wordsPerMinute = 200) => {
  if (!content) return 5;
  const wordCount = content.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text with ellipsis
 */
export const truncateText = (text, maxLength = 120) => {
  if (!text || text.length <= maxLength) return text || '';
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Get image URL with fallback placeholder
 * @param {string} imagePath - Original image path
 * @param {string} title - Blog title for placeholder
 * @returns {string} Image URL or placeholder
 */
export const getBlogImageUrl = (imagePath, title) => {
  if (!imagePath) {
    return `https://via.placeholder.com/800x600?text=${encodeURIComponent(title || 'Blog Post')}`;
  }
  
  // If it's a full URL, return as is
  if (imagePath.startsWith('http')) return imagePath;
  
  // Otherwise, assume it's a local path
  // Adjust this logic based on your image serving strategy
  return imagePath;
};

/**
 * Generate meta description from excerpt or content
 * @param {string} excerpt - Blog excerpt
 * @param {string} content - Blog content
 * @returns {string} Meta description
 */
export const generateMetaDescription = (excerpt, content) => {
  if (excerpt) return truncateText(excerpt, 160);
  if (content) return truncateText(content, 160);
  return 'Read our latest blog post about technology and innovation';
};

/**
 * Get category display name with fallback
 * @param {Object|string} category - Category object or string
 * @returns {string} Category name
 */
export const getCategoryName = (category) => {
  if (!category) return 'Uncategorized';
  return typeof category === 'string' ? category : category.name || 'Uncategorized';
};

/**
 * Get category slug with fallback
 * @param {Object|string} category - Category object or string
 * @returns {string} Category slug
 */
export const getCategorySlug = (category) => {
  if (!category) return 'uncategorized';
  return typeof category === 'string' ? category : category.slug || 'uncategorized';
};